import { autoinject, transient } from "aurelia-framework";
import { SurvivorshipRulesClientService } from "../../../../services/modules/survivorship-rules.service";
import { MdmDataTypeModel, MDRSurvivorshipRuleModel, DataSourceModel } from "../../../../models/survivorship-rules/survivorship-rules.models";
import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";

@autoinject
@transient()
export class SurvivorshipRules {

    public dataTypes: MdmDataTypeModel[];
    public selectedDataType: MdmDataTypeModel;
    public originalRules: MDRSurvivorshipRuleModel[];
    public dataSources: { [id: number]: string } = {};
    public rulesByProperty: { name: string, displayName: string, rules: MDRSurvivorshipRuleModel[] }[] = [];

    constructor(private survivorshipRulesClientService: SurvivorshipRulesClientService,
        private notificationClientService: NotificationClientService) { }

    activate() {
        this.survivorshipRulesClientService.getDataTypes().then(response => {
            this.dataTypes = response.dataTypes;
        });
    }

    public selectDataType(dataType: MdmDataTypeModel) {
        this.selectedDataType = dataType;
        this.survivorshipRulesClientService.getSurvivorshipRules(this.selectedDataType.dataTypeId).then(response => {

            this.onRulesReady(response.rules, response.dataSources);
        });
    }

    private onRulesReady(rules: MDRSurvivorshipRuleModel[], dataSources: DataSourceModel[]) {
        this.originalRules = rules;
        this.dataSources = {};
        dataSources.forEach(dataSource => {
            this.dataSources[dataSource.dataSourceId] = dataSource.name;
        });
        const rulesByProperty = [];
        Enumerable.from(CloneHelper.cloneObject(this.originalRules)).groupBy(z => z.columnName).forEach(set => {
            rulesByProperty.push({
                name: set.key(),
                displayName: this.insertSpaces(set.key()),
                rules: Enumerable.from(set.toArray()).orderBy(z => z.priority).toArray()
            });
        });
        this.rulesByProperty = rulesByProperty;
    }

    public changePriority(rule: MDRSurvivorshipRuleModel, direction: number) {

        const rulesSet = this.rulesByProperty.filter(z => z.name == rule.columnName)[0];
        if (direction == 1) {
            const nextRule = Enumerable.from(rulesSet.rules).firstOrDefault(z => z.priority > rule.priority);
            nextRule.priority--;
            rule.priority++;
        } else {
            const prevRule = Enumerable.from(rulesSet.rules).lastOrDefault(z => z.priority < rule.priority);
            prevRule.priority++;
            rule.priority--;
        }
        rulesSet.rules = Enumerable.from(rulesSet.rules).orderBy(z => z.priority).toArray();
    }

    public save() {
        return new Promise<void>((resolve, reject) => {
            let modifiedRules = Enumerable.from(this.rulesByProperty).selectMany(z => z.rules).toArray();
            modifiedRules = Enumerable.from(modifiedRules).join(this.originalRules, z => z.mdrSurvivorshipRuleId, z => z.mdrSurvivorshipRuleId, (modifiedRule, originalRule) => ({ modifiedRule, originalRule }))
                .where(z => z.originalRule.priority != z.modifiedRule.priority)
                .select(z => z.modifiedRule)
                .toArray();

            const rulesToSave = Enumerable.from(modifiedRules).select(z => ({ priority: z.priority, mdrSurvivorshipRuleId: z.mdrSurvivorshipRuleId })).toArray();
            this.survivorshipRulesClientService.saveSurvivorshipRules(rulesToSave).then(response => {
                if (response.isSuccess) {
                    this.notificationClientService.show("Rules Save", "", NotificationType.Success);
                } else {
                    this.notificationClientService.show("Failed to save rules", "", NotificationType.Error);
                }
                this.selectDataType(this.selectedDataType);
                resolve();
            }).catch(() => {
                this.notificationClientService.show("Failed to save rules", "", NotificationType.Error);
                reject();
            });
        });
    }

    insertSpaces(str: string) {
        str = str.replace(/([a-z])([A-Z])/g, '$1 $2');
        str = str.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
        return str;
    }
}
